import { flexComponentMap } from "components/flexFramework/FlexComponents/flexComponentMap";
import { Logger } from "bernie-logger";
import { ServerSideLogger } from "components/utility/ServerSideLogger";
import { UNSUPPORTED_MODULE } from "config/systemEvents";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { FlexModuleModelStore, FlexModuleModel } from "src/stores/FlexModuleModelStore";
import { LocalizedComponentProps } from "bernie-l10n";
import * as React from "react";
import { FlexComponentMapOptions } from "components/flexFramework/FlexComponents/typings";

export interface ComponentData {
  id?: string;
  name: string;
  fmId?: number;
  fmTitleId?: number;
  view?: string;
  scene?: string;
  children?: NewBlossomComponent[];
  flexModuleModel?: FlexModuleModel;
}

export interface ComponentProps extends LocalizedComponentProps {
  context: ExtendedContextStore;
}

export class NewBlossomComponent {
  public id: string;
  public name: string;
  public fmId?: number;
  public fmTitleId?: number;
  public view?: string;
  public scene?: string;
  public children?: NewBlossomComponent[];
  public componentMapping?: FlexComponentMapOptions;
  public getModel: () => {};
  public isMicroserviceResponsePending: () => boolean;

  public constructor(constructorProps: {
    componentData: ComponentData;
    context?: ExtendedContextStore;
    flexModuleModelStore: FlexModuleModelStore;
    logger?: Logger;
  }) {
    const { componentData, context, flexModuleModelStore, logger } = constructorProps;
    const { id, name, fmId, fmTitleId, view, scene, children, flexModuleModel } = componentData;

    this.id = !id ? (fmId ? `${name}${fmId}` : `${name}${Math.round(Math.random() * 100000)}`) : id;
    this.name = name;
    this.fmId = fmId;
    this.fmTitleId = fmTitleId;
    this.view = view;
    this.scene = scene;
    this.children = children;

    this.componentMapping = this.getComponentMapping();

    if (this.componentMapping === undefined && logger && context) {
      this.logUnsupportedModule(logger, context);
    } else if (flexModuleModel) {
      flexModuleModelStore.updateModelMap(this.id, flexModuleModel);
    }

    this.getModel = () => flexModuleModelStore.modelMap.get(this.id)?.model || {};

    this.isMicroserviceResponsePending = () => {
      const modelMapEntry = flexModuleModelStore.modelMap.get(this.id);

      return !modelMapEntry ? true : modelMapEntry.responsePending;
    };
  }

  /**
   * Renders the BlossomComponent using it's reactComponent
   * @param { ComponentProps } componentProps
   * @returns React.Component
   * @memberof NewBlossomComponent
   */
  public render = (componentProps: ComponentProps) => {
    if (!this.componentMapping) {
      return null;
    } else {
      return React.createElement(this.componentMapping.codeSplit as any, {
        l10n: componentProps.l10n,
        context: componentProps.context,
        blossomComponent: this,
        key: this.id,
      });
    }
  };

  /**
   * do all of the BlossomComponent's children have pending flex microservice responses?
   * @returns boolean
   * @memberof NewBlossomComponent
   */
  public areAllChildrenPending = () => {
    if (!this.hasChildren()) {
      return false;
    }

    return !this.children!.some((child) => !child.isMicroserviceResponsePending());
  };

  /**
   * does the BlossomComponent have any children
   * @return boolean
   * @memberof NewBlossomComponent
   */
  public hasChildren = () => !!(this.children && this.children.length > 0);

  /**
   * returns corresponding mapping object from flexComponetMap using it's name and view
   * Naming convention: <module name>_<view>
   * @returns FlexComponentMapOptions|undefined
   */
  private getComponentMapping = () => {
    if (!flexComponentMap.has(this.name)) {
      return undefined;
    }

    const viewSpecificComponentName = `${this.name}_${this.view}`;
    if (this.view !== undefined && flexComponentMap.has(viewSpecificComponentName)) {
      return flexComponentMap.get(viewSpecificComponentName);
    }

    return flexComponentMap.get(this.name);
  };

  /**
   * Takes the name of a Flex component found in a composition that had no corresponding Flex UI Component, and reports it to the server side logger
   */
  private logUnsupportedModule = (logger: Logger, context: ExtendedContextStore) => {
    const contextualInfo = Object.entries({
      pageType: context.searchContext?.pageType,
      deviceId: context.deviceId,
      userAgent: context.userAgent,
      langId: context.langId,
      locale: context.locale,
      siteId: context.site?.id,
      requestId: context.traceInfo && context.traceInfo["Trace-ID"],
    })
      .map((key, value) => `${key}=\"${value}\"`)
      .toString();

    ServerSideLogger(logger!).logEvent(
      UNSUPPORTED_MODULE,
      `moduleName=${this.name} is not currently supported by this view layer application`,
      contextualInfo
    );
  };
}
